// eval-window.js - the Season Plan's fortnightly pillar rotation, as a pure
// client-side function.
//
// A plan is { season_start, season_end, cadence_days, pillar_order[],
//             team_exclusions{}, breaks[] }.
// The window index = whole cadence periods elapsed since season_start; the
// open pillar is pillar_order[index % length]. A pillar listed in
// team_exclusions[teamId] for the current window means "nothing scheduled for
// that team this fortnight".
//
// BREAKS pause the rotation. The club evaluates around the outdoor calendar, so
// the indoor block (and any other gap the director sets) is dead time: no
// window is open, AND those days do not advance the rotation. When play
// resumes, the next pillar is the one that was coming up, not whichever pillar
// the calendar would have drifted to. Without that, a three-month indoor pause
// would silently skip six or seven pillars.

const DAY_MS = 86400000;
const ms = (d) => Date.parse(String(d).slice(0, 10) + 'T00:00:00Z');

/** Normalise + merge break ranges so overlaps are never counted twice. */
function mergedBreaks(breaks) {
  const out = [];
  for (const b of breaks || []) {
    const from = ms(b?.from), to = ms(b?.to);
    if (!Number.isFinite(from) || !Number.isFinite(to) || to < from) continue;
    out.push([from, to]);
  }
  out.sort((a, z) => a[0] - z[0]);
  const merged = [];
  for (const r of out) {
    const last = merged[merged.length - 1];
    if (last && r[0] <= last[1] + DAY_MS) last[1] = Math.max(last[1], r[1]);
    else merged.push([...r]);
  }
  return merged;
}

/** Whole days inside a break that have already elapsed (start .. yesterday). */
function pausedDaysBefore(ranges, startMs, todayMs) {
  let n = 0;
  for (const [from, to] of ranges) {
    const lo = Math.max(from, startMs);
    const hi = Math.min(to, todayMs - DAY_MS);   // today itself never counts
    if (hi >= lo) n += Math.round((hi - lo) / DAY_MS) + 1;
  }
  return n;
}

/**
 * Which pillar is open for a team on a given date.
 * @returns { pillar: string|null,
 *            reason: 'open'|'before_season'|'after_season'|'break'|'excluded'|'no_plan' }
 */
export function currentPillar(plan, teamId, todayIso) {
  if (!plan || !Array.isArray(plan.pillar_order) || !plan.pillar_order.length) {
    return { pillar: null, reason: 'no_plan' };
  }
  const today = (todayIso || '').slice(0, 10);
  if (plan.season_start && today < plan.season_start) {
    return { pillar: null, reason: 'before_season' };
  }
  if (plan.season_end && today > plan.season_end) {
    return { pillar: null, reason: 'after_season' };
  }

  const cadence = Math.max(1, Number(plan.cadence_days) || 14);
  const startMs = ms(plan.season_start);
  const todayMs = ms(today);
  const order   = plan.pillar_order;
  const ranges  = mergedBreaks(plan.breaks);

  // Inside a break: nothing is scheduled at all.
  if (ranges.some(([from, to]) => todayMs >= from && todayMs <= to)) {
    return { pillar: null, reason: 'break' };
  }

  const elapsed   = Math.floor((todayMs - startMs) / DAY_MS);
  const effective = Math.max(0, elapsed - pausedDaysBefore(ranges, startMs, todayMs));
  const winIdx    = Math.floor(effective / cadence);
  const pillar    = order[((winIdx % order.length) + order.length) % order.length];

  const excluded = (plan.team_exclusions || {})[teamId];
  if (Array.isArray(excluded) && excluded.includes(pillar)) {
    return { pillar: null, reason: 'excluded' };
  }
  return { pillar, reason: 'open' };
}

/**
 * The whole season as a list of windows, for showing the director what is
 * scheduled when.
 *
 * Deliberately walks the season a day at a time and asks currentPillar(),
 * rather than re-deriving the dates from cadence and breaks. Re-deriving would
 * be faster and would eventually disagree: breaks pause the rotation, so the
 * arithmetic is fiddly enough that two copies of it drift, and then the app
 * shows a schedule it does not enforce. A season is under 400 days, so the
 * cost of being provably identical is nothing.
 *
 * @param {object} plan
 * @param {string} [teamId]  apply this team's exclusions; omit for a club view
 * @param {string} [todayIso] for the past/open/future flag; omit to skip it
 * @returns {Array<{pillar: string|null, reason: string, from: string, to: string,
 *                  days: number, status: 'past'|'open'|'future'|null}>}
 */
export function planWindows(plan, teamId, todayIso) {
  if (!plan || !plan.season_start || !plan.season_end) return [];
  const startMs = ms(plan.season_start);
  const endMs   = ms(plan.season_end);
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs < startMs) return [];

  const today = (todayIso || '').slice(0, 10) || null;
  const out = [];
  for (let t = startMs; t <= endMs; t += DAY_MS) {
    const day = new Date(t).toISOString().slice(0, 10);
    const { pillar, reason } = currentPillar(plan, teamId, day);
    const last = out[out.length - 1];
    // A run continues while both the pillar and the reason hold, so a break or
    // an excluded fortnight shows as its own row rather than merging into the
    // pillar either side of it.
    if (last && last.pillar === pillar && last.reason === reason) last.to = day;
    else out.push({ pillar, reason, from: day, to: day });
  }

  return out.map(w => ({
    ...w,
    days: Math.round((ms(w.to) - ms(w.from)) / DAY_MS) + 1,
    status: !today ? null
          : today > w.to   ? 'past'
          : today < w.from ? 'future'
          : 'open',
  }));
}
